import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { l402Api, DocumentItem, SectionItem } from '../../services/l402Api';
import './L402Form.css';

// ---- Component ----
const L402Form: React.FC = () => {
  const navigate = useNavigate();

  const [category, setCategory]     = useState('');
  const [keyword, setKeyword]       = useState('');
  const [items, setItems]           = useState<DocumentItem[]>([]);
  const [loading, setLoading]       = useState(false);
  const [queried, setQueried]       = useState(false);

  // Section modal state
  const [sectionVisible, setSectionVisible]   = useState(false);
  const [sectionDoc, setSectionDoc]           = useState<DocumentItem | null>(null);
  const [sectionItems, setSectionItems]       = useState<SectionItem[]>([]);
  const [sectionLoading, setSectionLoading]   = useState(false);

  // ---- Query ----
  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await l402Api.queryDocuments({ category, keyword });
      setItems(res.items || []);
      setQueried(true);
      if ((res.items || []).length === 0) {
        message.info('查無符合條件的文件');
      } else {
        message.success(`查詢到 ${res.items.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [category, keyword]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => { if (e.key === 'Enter') onQuery(); },
    [onQuery]
  );

  // ---- Open section modal ----
  const openSections = useCallback(async (doc: DocumentItem) => {
    setSectionDoc(doc);
    setSectionVisible(true);
    setSectionLoading(true);
    try {
      const res = await l402Api.querySections(doc.fl020_01);
      setSectionItems(res.items || []);
    } catch {
      message.error('載入章節失敗');
      setSectionItems([]);
    } finally {
      setSectionLoading(false);
    }
  }, []);

  // ---- Table columns ----
  const columns: ColumnsType<DocumentItem> = [
    {
      title: '文件編號',
      dataIndex: 'fl020_01',
      key: 'fl020_01',
      width: 120,
      fixed: 'left',
      render: (v: string, record) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => openSections(record)}>
          {v}
        </Button>
      ),
    },
    {
      title: '文件名稱',
      dataIndex: 'fl020_11',
      key: 'fl020_11',
      ellipsis: true,
    },
    {
      title: '類別代碼',
      dataIndex: 'fl020_12',
      key: 'fl020_12',
      width: 90,
    },
    {
      title: '版本',
      dataIndex: 'fl020_13',
      key: 'fl020_13',
      width: 80,
    },
    {
      title: '建立日期',
      dataIndex: 'fl020_14',
      key: 'fl020_14',
      width: 110,
    },
    {
      title: '建立人員',
      dataIndex: 'fl020_15',
      key: 'fl020_15',
      width: 90,
    },
    {
      title: '章節',
      key: 'sections',
      width: 80,
      align: 'center',
      render: (_: unknown, record) => (
        <Button size="small" onClick={() => openSections(record)}>
          查看
        </Button>
      ),
    },
  ];

  const sectionColumns: ColumnsType<SectionItem> = [
    { title: '序號', dataIndex: 'fl120_02', key: 'fl120_02', width: 70, align: 'center' },
    { title: '章節名稱', dataIndex: 'fl120_11', key: 'fl120_11', width: 160 },
    { title: '內容摘要', dataIndex: 'fl120_12', key: 'fl120_12', ellipsis: true },
    { title: '附件路徑', dataIndex: 'fl120_13', key: 'fl120_13', ellipsis: true, width: 180 },
  ];

  // ---- Render ----
  return (
    <div className="l402-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>文件管理</a>
              <span>/</span>
              <span className="current">文件查詢</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>文件查詢</h1>
            <span className="badge">L402</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label>文件類別:</label>
          <Input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="類別代碼（最多4碼）"
            style={{ width: 140 }}
            maxLength={4}
            allowClear
          />

          <div className="group-divider" />

          <label>關鍵字:</label>
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="文件編號或名稱"
            style={{ width: 220 }}
            allowClear
          />

          <Button type="primary" onClick={onQuery} loading={loading}>
            查詢
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
              </div>
            </div>
          )}

          {queried ? (
            <Table<DocumentItem>
              columns={columns}
              dataSource={items}
              rowKey="fl020_01"
              pagination={{ pageSize: 50, showSizeChanger: true }}
              size="small"
              scroll={{ x: 900, y: 420 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">請設定查詢條件並點擊「查詢」</div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button danger size="large" onClick={() => navigate('/erp')}>
            離開
          </Button>
        </div>
      </div>

      {/* Section Modal */}
      <Modal
        title={sectionDoc ? `章節明細 — ${sectionDoc.fl020_01} ${sectionDoc.fl020_11}` : '章節明細'}
        open={sectionVisible}
        onCancel={() => { setSectionVisible(false); setSectionItems([]); }}
        footer={null}
        width={800}
      >
        <Table<SectionItem>
          columns={sectionColumns}
          dataSource={sectionItems}
          rowKey="fl120_02"
          loading={sectionLoading}
          pagination={false}
          size="small"
          scroll={{ y: 400 }}
          locale={{ emptyText: '此文件無章節資料' }}
        />
      </Modal>
    </div>
  );
};

export default L402Form;
